import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreditCard, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const LiveModePaymentTester: React.FC = () => {
  const [testEmail, setTestEmail] = useState('test@carekonnect.com');
  const [selectedTier, setSelectedTier] = useState('tier1');
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState<any[]>([]);
  const { toast } = useToast();

  const tiers = [
    { id: 'tier1', name: 'Tier 1 - 1 Location', price: '$100/month' },
    { id: 'tier2', name: 'Tier 2 - 2 Locations', price: '$150/month' },
    { id: 'tier3', name: 'Tier 3 - 3+ Locations', price: '$225/month+' }
  ];

  const testStripeConnection = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          tier: selectedTier, 
          testMode: true 
        }
      });

      if (error) {
        setTestResults(prev => [...prev, {
          test: 'Stripe Connection',
          status: 'failed',
          message: error.message,
          timestamp: new Date().toLocaleTimeString()
        }]);
        toast({
          title: "Stripe Connection Failed",
          description: error.message,
          variant: "destructive"
        });
      } else {
        const isLiveMode = data?.url && !data.url.includes('test');
        setTestResults(prev => [...prev, {
          test: 'Stripe Connection',
          status: isLiveMode ? 'success' : 'warning',
          message: isLiveMode ? 'Live mode confirmed' : 'Test mode detected',
          timestamp: new Date().toLocaleTimeString(),
          url: data?.url
        }]);
        
        toast({
          title: isLiveMode ? "Live Mode Confirmed" : "Test Mode Detected",
          description: isLiveMode ? "Stripe is configured for live payments" : "Stripe is still in test mode"
        });
      }
    } catch (error) {
      console.error('Test failed:', error);
      setTestResults(prev => [...prev, {
        test: 'Stripe Connection',
        status: 'failed',
        message: 'Unexpected error occurred',
        timestamp: new Date().toLocaleTimeString()
      }]);
    }
    setIsLoading(false);
  };

  const testWebhookEndpoint = async () => {
    setIsLoading(true);
    try {
      // Test webhook endpoint accessibility
      const response = await fetch(`${window.location.origin}/api/webhooks/stripe`, {
        method: 'OPTIONS'
      });

      setTestResults(prev => [...prev, {
        test: 'Webhook Endpoint',
        status: response.ok ? 'success' : 'failed',
        message: response.ok ? 'Webhook endpoint is accessible' : 'Webhook endpoint not accessible',
        timestamp: new Date().toLocaleTimeString()
      }]);

      toast({
        title: response.ok ? "Webhook Accessible" : "Webhook Issue",
        description: response.ok ? "Webhook endpoint is properly configured" : "Webhook endpoint may need configuration"
      });
    } catch (error) {
      setTestResults(prev => [...prev, {
        test: 'Webhook Endpoint',
        status: 'failed',
        message: 'Could not reach webhook endpoint',
        timestamp: new Date().toLocaleTimeString()
      }]);
    }
    setIsLoading(false);
  };

  const testSubscriptionFlow = async () => {
    setIsLoading(true);
    try {
      // Test the complete subscription flow
      const { data, error } = await supabase.functions.invoke('check-subscription');

      if (error) {
        setTestResults(prev => [...prev, {
          test: 'Subscription Flow',
          status: 'failed',
          message: error.message,
          timestamp: new Date().toLocaleTimeString()
        }]);
      } else {
        setTestResults(prev => [...prev, {
          test: 'Subscription Flow',
          status: 'success',
          message: 'Subscription system is operational',
          timestamp: new Date().toLocaleTimeString()
        }]);
      }
    } catch (error) {
      setTestResults(prev => [...prev, {
        test: 'Subscription Flow',
        status: 'failed',
        message: 'Subscription flow test failed',
        timestamp: new Date().toLocaleTimeString()
      }]);
    }
    setIsLoading(false);
  };

  const createTestCheckout = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          tier: selectedTier,
          additionalLocations: selectedTier === 'tier3' ? 1 : 0
        }
      });

      if (error) {
        toast({
          title: "Checkout Creation Failed",
          description: error.message,
          variant: "destructive"
        });
      } else if (data?.url) {
        // Open checkout in new tab for testing
        window.open(data.url, '_blank');
        toast({
          title: "Test Checkout Created",
          description: "Checkout opened in new tab. Use test card: 4242424242424242"
        });
        
        setTestResults(prev => [...prev, {
          test: 'Live Checkout Creation',
          status: 'success',
          message: 'Checkout session created successfully',
          timestamp: new Date().toLocaleTimeString(),
          url: data.url
        }]);
      }
    } catch (error) {
      console.error('Checkout creation failed:', error);
      toast({
        title: "Checkout Error",
        description: "Failed to create checkout session",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const runAllTests = async () => {
    setTestResults([]);
    await testStripeConnection();
    await new Promise(resolve => setTimeout(resolve, 1000));
    await testWebhookEndpoint();
    await new Promise(resolve => setTimeout(resolve, 1000));
    await testSubscriptionFlow();
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-800">Warning</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            Live Mode Payment Testing
          </CardTitle>
          <CardDescription>
            Test Stripe live mode configuration and payment flows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> This will test live mode Stripe integration. 
              Use test cards for checkout testing to avoid real charges.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Test Email</Label>
              <Input
                id="test-email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tier-select">Test Subscription Tier</Label>
              <Select value={selectedTier} onValueChange={setSelectedTier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map(tier => (
                    <SelectItem key={tier.id} value={tier.id}>
                      {tier.name} - {tier.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={runAllTests} disabled={isLoading}>
              Run All Tests
            </Button>
            <Button onClick={testStripeConnection} variant="outline" disabled={isLoading}>
              Test Connection
            </Button>
            <Button onClick={testWebhookEndpoint} variant="outline" disabled={isLoading}>
              Test Webhook
            </Button>
            <Button onClick={testSubscriptionFlow} variant="outline" disabled={isLoading}>
              Test Subscription
            </Button>
            <Button onClick={createTestCheckout} variant="outline" disabled={isLoading}>
              Create Test Checkout
            </Button>
            <Button onClick={clearResults} variant="ghost" size="sm">
              Clear Results
            </Button>
          </div>
        </CardContent>
      </Card>

      {testResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Results</CardTitle>
            <CardDescription>
              Latest test execution results
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                  {result.status === 'success' ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{result.test}</span>
                      {getStatusBadge(result.status)}
                      <span className="text-xs text-gray-500">{result.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-600">{result.message}</p>
                    {result.url && (
                      <a 
                        href={result.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open Checkout URL
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Test Cards for Live Mode Testing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-sm space-y-1">
            <div className="font-medium">Stripe Test Cards (Safe for Live Mode Testing):</div>
            <div>• Success: <code className="bg-gray-100 px-1 rounded">4242424242424242</code></div>
            <div>• Decline: <code className="bg-gray-100 px-1 rounded">4000000000000002</code></div>
            <div>• Requires Authentication: <code className="bg-gray-100 px-1 rounded">4000002760003184</code></div>
            <div className="text-xs text-gray-600 mt-2">
              Use any future expiry date, any 3-digit CVC, and any billing postal code.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Production Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <input type="checkbox" className="rounded" />
              <span>Stripe secret key updated to live mode</span>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="rounded" />
              <span>Webhook endpoints configured for production domain</span>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="rounded" />
              <span>Test payment flows with real cards (small amounts)</span>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="rounded" />
              <span>Verify subscription creation and management</span>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="rounded" />
              <span>Test payment failure handling</span>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" className="rounded" />
              <span>Verify customer portal access</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveModePaymentTester;